import { type RefObject, type TransitionEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Durations and easing come from `ui-animations`: a resize of an element
 * already on screen is `ease-in-out`, and the exit runs 20-30% faster than the
 * enter. **Keep these in sync with `FactRow.module.css`** — the CSS owns the
 * transition, these constants only size the settle backstop.
 */
export const EXPAND_MS = 200;
export const COLLAPSE_MS = 150;

/** Slack on the backstop so it never beats a real `transitionend`. */
const SETTLE_SLACK_MS = 50;

type Direction = 'expand' | 'collapse';

function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Expand/collapse for a one-line-collapsed text block, animating `height`
 * between a measured pixel value and the CSS resting height.
 *
 * Measuring the height is what makes the animation work outside Chromium.
 * `interpolate-size: allow-keywords` with `height: 1lh ↔ auto` is the sanctioned
 * modern form but is Chromium-only; `grid-template-rows: 0fr ↔ 1fr` interpolates
 * only to zero, where this collapses to one visible line of the same paragraph;
 * and easing `max-height` against a cap makes short values lag visibly.
 *
 * Collapsing runs in two steps so the closing animation looks right: while the
 * height eases down the text stays wrapped (`isSettling`), and the one-line
 * `nowrap` + fade treatment is applied only once the transition has finished.
 * `transitionend` drives that, with a timeout backstop for the cases where it
 * never arrives — most importantly `prefers-reduced-motion`, where there is no
 * transition at all and the row settles synchronously instead.
 *
 * The transition itself is armed only while `isAnimating`, so nothing animates
 * on mount, on the `document.fonts.ready` re-measure, or on a sidebar resize.
 */
export function useExpandCollapse<T extends HTMLElement>(elementRef: RefObject<T | null>) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [direction, setDirection] = useState<Direction | null>(null);
    const settleTimeoutRef = useRef<number | undefined>(undefined);
    /** Height the row animates *from*, captured before React re-renders it. */
    const fromHeightRef = useRef<number | null>(null);

    const settle = useCallback(() => {
        window.clearTimeout(settleTimeoutRef.current);
        fromHeightRef.current = null;
        const el = elementRef.current;
        if (el) {
            el.style.height = '';
        }
        setDirection(null);
    }, [elementRef]);

    useEffect(() => () => window.clearTimeout(settleTimeoutRef.current), []);

    const toggle = useCallback(() => {
        window.clearTimeout(settleTimeoutRef.current);
        const el = elementRef.current;

        if (!el || prefersReducedMotion()) {
            fromHeightRef.current = null;
            if (el) {
                el.style.height = '';
            }
            setDirection(null);
            setIsExpanded(!isExpanded);
            return;
        }

        // Read the current height before the class change: mid-animation this is
        // the in-flight height, which is what makes a reversal retarget rather
        // than jump.
        fromHeightRef.current = el.getBoundingClientRect().height;
        setDirection(isExpanded ? 'collapse' : 'expand');
        setIsExpanded(!isExpanded);
    }, [elementRef, isExpanded]);

    useLayoutEffect(() => {
        const el = elementRef.current;
        const fromHeight = fromHeightRef.current;
        if (!el || fromHeight === null || direction === null) {
            return;
        }
        fromHeightRef.current = null;

        // The row is already rendered in its target state, so its natural height
        // is the expand target. Pin the start height, flush it, then move to the
        // target: explicit px on expand, the CSS one-line height on collapse.
        const toHeight = direction === 'expand' ? el.getBoundingClientRect().height : null;
        el.style.height = `${fromHeight}px`;
        void el.offsetHeight;
        el.style.height = toHeight === null ? '' : `${toHeight}px`;

        const duration = direction === 'expand' ? EXPAND_MS : COLLAPSE_MS;
        settleTimeoutRef.current = window.setTimeout(settle, duration + SETTLE_SLACK_MS);
    }, [direction, isExpanded, elementRef, settle]);

    const onTransitionEnd = useCallback((event: TransitionEvent) => {
        if (event.propertyName === 'height') {
            settle();
        }
    }, [settle]);

    return {
        isExpanded,
        /** Collapsing: the height is easing down over still-wrapped text. */
        isSettling: direction === 'collapse',
        /** Arms the height transition; false at rest so measurement can't animate. */
        isAnimating: direction !== null,
        toggle,
        onTransitionEnd,
    };
}
